import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Announcements.css';

const API_BASE = process.env.REACT_APP_API_BASE;

const Announcements = ({ userData, hasPermission }) => {
    const [announcements, setAnnouncements] = useState([]);
    const [channelInfo, setChannelInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [newContent, setNewContent] = useState('');
    const [target, setTarget] = useState('both');
    const [submitting, setSubmitting] = useState(false);

    const canCreate = hasPermission ? hasPermission('manage_systems') || hasPermission('manage_announcements') : false;

    // useCallback verhindert unnötige Re-renders
    const fetchAnnouncements = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const token = localStorage.getItem('discord_token');
            if (!token) {
                throw new Error('Nicht autorisiert. Bitte melde dich erneut an.');
            }

            const response = await axios.get(`${API_BASE}/api/announcements/discord`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10000 // 10 Sekunden Timeout
            });

            // Sicherstellen, dass wir ein Array haben
            const data = response.data;
            const discordAnnouncements = Array.isArray(data.announcements) ? data.announcements : [];

            setAnnouncements(discordAnnouncements);
            setChannelInfo({
                name: data.channel_name || 'Unbekannt',
                id: data.channel_id
            });

        } catch (err) {
            console.error('API Error:', err);
            const message = err.response?.data?.error || err.message || 'Server nicht erreichbar';
            setError(message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAnnouncements();
    }, [fetchAnnouncements]);

    const formatContent = (content) => {
        if (!content) return '';
        return content
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/__(.*?)__/g, '<u>$1</u>')
            .replace(/`(.*?)`/g, '<code>$1</code>')
            .replace(/\n/g, '<br>');
    };

    const handleCreate = async () => {
        if (!newContent.trim() || submitting) return;
        setSubmitting(true);
        setError(null);
        try {
            const token = localStorage.getItem('discord_token');
            const res = await fetch(`${API_BASE}/api/announcements`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    content: newContent,
                    author: {
                        username: userData?.username || 'Unbekannt',
                        avatarURL: userData?.avatarUrl || 'https://cdn.discordapp.com/embed/avatars/0.png'
                    },
                    target
                })
            });
            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Fehler beim Erstellen');
            }
            setNewContent('');
            setShowForm(false);
            fetchAnnouncements();
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return (
        <div className="dashboard-main">
            <div className="announcements-container">
                <div className="announcements-card">
                    <div className="announcements-loading">
                        <div className="spinner"></div>
                        <p>Lade Ankündigungen...</p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="dashboard-main">
            <div className="announcements-container">
                <div className="announcements-card">
                    <div className="announcements-hero">
                        <div className="announcements-hero-header">
                            <div className="announcements-hero-icon">
                                <i className="fas fa-bullhorn"></i>
                            </div>
                            <div className="announcements-hero-title">
                                <h2>📢 Ankündigungen</h2>
                                <p>{channelInfo?.name ? `Kanal: #${channelInfo.name}` : 'News aus Discord'}</p>
                            </div>
                        </div>
                        <button className="announcements-refresh-btn" onClick={fetchAnnouncements}>
                            <i className="fas fa-sync-alt"></i>
                            Aktualisieren
                        </button>
                    </div>

                    {error && (
                        <div className="announcements-error">
                            <strong>Fehler:</strong> {error}
                        </div>
                    )}

                    {canCreate && (
                        <div className="announcements-create">
                            {!showForm ? (
                                <button className="announcements-create-btn" onClick={() => setShowForm(true)}>
                                    <i className="fas fa-plus"></i> Neue Ankündigung
                                </button>
                            ) : (
                                <div className="announcements-form">
                                    <h3>Neue Ankündigung</h3>
                                    <textarea
                                        className="announcements-textarea"
                                        value={newContent}
                                        onChange={(e) => setNewContent(e.target.value)}
                                        placeholder="Inhalt der Ankündigung..."
                                        rows={4}
                                    />
                                    <div className="announcements-target-row">
                                        <label>Ziel:</label>
                                        <select value={target} onChange={(e) => setTarget(e.target.value)}>
                                            <option value="both">Dashboard + Discord</option>
                                            <option value="dashboard">Nur Dashboard</option>
                                            <option value="discord">Nur Discord</option>
                                        </select>
                                    </div>
                                    <div className="announcements-form-actions">
                                        <button className="btn btn-primary" onClick={handleCreate} disabled={submitting || !newContent.trim()}>
                                            {submitting ? 'Senden...' : 'Veröffentlichen'}
                                        </button>
                                        <button className="btn btn-secondary" onClick={() => { setShowForm(false); setNewContent(''); }}>
                                            Abbrechen
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="announcements-body">
                        {announcements.length === 0 && !error ? (
                            <div className="announcements-empty">Keine Nachrichten gefunden.</div>
                        ) : (
                            <div className="announcements-list">
                                {announcements.map((msg) => (
                                    <div key={msg.id} className="announcement-card">
                                        <div className="announcement-header">
                                            <img 
                                                src={msg.author?.avatarURL || 'https://cdn.discordapp.com/embed/avatars/0.png'} 
                                                alt="Avatar" 
                                                className="announcement-author-avatar" 
                                            />
                                            <div className="announcement-author-info">
                                                <span className="announcement-author-name">{msg.author?.nick || msg.author?.username}</span>
                                                <span className="announcement-time">
                                                    {new Date(msg.createdAt).toLocaleString('de-DE')}
                                                </span>
                                            </div>
                                        </div>
                                        <div 
                                            className="announcement-content-body"
                                            dangerouslySetInnerHTML={{ __html: formatContent(msg.content) }}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Announcements;
